import { useEffect, useState } from 'react';
import { getCookie } from '@libraries/common/cookie';

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    analytics: any;
    steadioAnalytics: any;
    SteadioApp: any;
    webkit?: any;
  }
}

export default function useInitAnalytics() {
  const [queue, setQueue] = useState<(() => void)[]>([]);

  useEffect(() => {
    if (queue.length === 0) {
      return;
    }

    const interval = setInterval(() => {
      // eslint-disable-next-line no-extra-boolean-cast
      if (!!window.branch) {
        queue.forEach((func) => func());
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [queue]);

  useEffect(() => {
    window.steadioAnalytics = {
      getAnonymousId: () => getCookie('ajs_anonymous_id'),
      identify(userId: string, properties: {}) {
        // 앱이 아닌 브라우저 환경에서 identify 실행
        if (
          typeof navigator !== 'undefined' &&
          navigator?.userAgent.indexOf('tumblbug-aos') < 0 &&
          navigator?.userAgent.indexOf('tumblbug-ios') < 0
        ) {
          // Segment
          window.analytics?.identify(userId, properties);

          // Branch
          window.branch?.setIdentity(userId);
        }
      },
      track(eventName: string, trackData: {}) {
        if (
          typeof navigator !== 'undefined' &&
          navigator?.userAgent.indexOf('tumblbug-aos') > -1
        ) {
          window.steadioAnalytics.trackDataAos('track', eventName, trackData);
        } else if (
          typeof navigator !== 'undefined' &&
          navigator?.userAgent.indexOf('tumblbug-ios') > -1
        ) {
          window.steadioAnalytics.trackDataIos('track', eventName, trackData);
        } else {
          // Segment
          window.analytics?.track(eventName, trackData);
          // branch
          window.steadioAnalytics.trackDataWebBranch(eventName, trackData);
        }
      },
      page(pageName: string, trackData: {}) {
        if (
          typeof navigator !== 'undefined' &&
          navigator?.userAgent.indexOf('tumblbug-aos') > -1
        ) {
          window.steadioAnalytics.trackDataAos('screen', pageName, trackData);
        } else if (
          typeof navigator !== 'undefined' &&
          navigator?.userAgent.indexOf('tumblbug-ios') > -1
        ) {
          window.steadioAnalytics.trackDataIos('screen', pageName, trackData);
        } else {
          window.analytics?.page(pageName, trackData);

          // branch
          window.steadioAnalytics.trackDataWebBranch(pageName, trackData);
        }
      },
      trackDataWebBranch: (name: string, properties: any) => {
        if (name === 'Signed Up Success') {
          window.branch?.logEvent(
            'COMPLETE_REGISTRATION',
            {
              ...properties,
              transaction_id: properties.user_id,
              registration_id: properties.user_id,
            },
            [],
            name,
          );
        } else if (name === 'Product Viewed') {
          const eventData = {
            transaction_id: properties.product_id,
            currency: 'KRW',
            revenue: properties.price,
          };
          const contentItems = [
            {
              $content_schema: 'COMMERCE_PRODUCT',
              $og_title: properties.name,
              $canonical_identifier: properties.category,
              $price: properties.price,
              $quantity: 1,
              $sku: properties.product_id,
              $product_name: properties.name,
              $product_brand: properties.creator_permalink,
            },
          ];
          if (!window.branch) {
            setQueue((prev) => [
              ...prev,
              ...[
                () =>
                  window.branch?.logEvent(
                    'VIEW_ITEMS',
                    eventData,
                    contentItems,
                    name,
                  ),
              ],
            ]);
          } else {
            window.branch?.logEvent(
              'VIEW_ITEMS',
              eventData,
              contentItems,
              name,
            );
          }
        } else if (name === 'Order Completed') {
          const product = properties.products[0];
          const eventData = {
            transaction_id: properties.order_id,
            currency: 'KRW',
            revenue: properties.revenue,
          };
          const contentItems = [
            {
              $content_schema: 'COMMERCE_PRODUCT',
              $og_title: product.name,
              $canonical_identifier: product.category,
              $price: product.price,
              $quantity: 1,
              $sku: product.sku,
              $product_name: product.name,
              $product_brand: product.creator_permalink,
            },
          ];
          window.branch?.logEvent('PURCHASE', eventData, contentItems, name);
        }
      },
      trackDataAos: (type: string, name: string, properties: any) => {
        try {
          // eslint-disable-next-line prefer-const
          let params = window.steadioAnalytics.makeParams(
            type,
            name,
            properties,
          );
          window.SteadioApp.setTrackData(JSON.stringify(params));
        } catch (error: any) {
          console.error('trackDataAos', error);
        }
      },
      bridge: (functionName: string, params: any) => {
        const userAgent = navigator.userAgent;

        if (userAgent.includes('tumblbug-aos')) {
          window.SteadioApp?.[functionName](JSON.stringify(params));
        }

        if (userAgent.includes('tumblbug-ios')) {
          window.webkit.messageHandlers?.[functionName].postMessage(
            JSON.stringify(params),
          );
        }
      },
      trackDataIos: (type: string, name: string, properties: any) => {
        const params = window.steadioAnalytics.makeParams(
          type,
          name,
          properties,
        );
        window.webkit.messageHandlers.setTrackData.postMessage(params);
      },
      openApp: (endpoint: any, params: any) => {
        let url = '';

        if (params) {
          url = `tumblbug://tumblbug.view/${endpoint}?${Object.entries(params)
            .map(([key, val]) => `${key}=${val}`)
            .join('&')}`;
        } else {
          url = `tumblbug://tumblbug.view/${endpoint}`;
        }

        if (
          typeof navigator !== 'undefined' &&
          navigator?.userAgent.indexOf('tumblbug-aos') > -1
        ) {
          window.steadioAnalytics.openApp(url);
        } else if (
          typeof navigator !== 'undefined' &&
          navigator?.userAgent.indexOf('tumblbug-ios') > -1
        ) {
          window.webkit.messageHandlers.openApp.postMessage(url);
        }
      },
      makeParams: (type: string, name: string, properties: any) => ({
        messageType: 'event',
        data: {
          eventType: type,
          eventName: name,
          eventProperties: properties,
        },
      }),
    };
  }, []);
}
